import React from "react";
import { useTranslation } from "react-i18next";
import Hero from "src/components/Hero/Hero";
import TitleSection from "src/components/TitleSection/TitleSection";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import ReactMarkdown from "react-markdown";
import styled from "styled-components";
import { Col, Row, Table } from "react-bootstrap";
import TournamentSubtabsContent from "src/pages/poker/tournaments/TournamentSubtabsContent";
import TournamentSectionalTabs from "src/components/SectionalTabs/TournamentSectionalTabs";
import { useHomepage } from "src/context/homepage/homepage.provider";
import EventFinished from "src/components/EventFinished";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

const TripleBarrelCashBlast2025AnniversaryEdition = () => {
  const { i18n, t } = useTranslation();
  const { tournamentsSubtabs } = useHomepage();
  const sectionalTabsContent = t(
    "tripleBarrelCashBlast2025AnniversaryEdition.content.sectionalTabsContent",
    { returnObjects: true }
  );
  const prizeTables = t("tripleBarrelCashBlast2025AnniversaryEdition.content.prizeTables", {
    returnObjects: true,
  });

  const dynamicSubTabs = sectionalTabsContent[`subTab${tournamentsSubtabs}`];
  return (
    <Div>
      <EventFinished
        scheduleKeyName="tripleBarrelCashBlast2025AnniversaryEdition"
        eventType="promotion"
      />
      <Hero
        image={t("tripleBarrelCashBlast2025AnniversaryEdition.hero.image")}
        bgImage={t("tripleBarrelCashBlast2025AnniversaryEdition.hero.bgImage")}
        imageAlt={t("tripleBarrelCashBlast2025AnniversaryEdition.hero.imageAlt")}
        fluidImage={"hero?.image" ? false : true}
      />
      <container-div>
        <TitleSection
          as={"h2"}
          title={t("tripleBarrelCashBlast2025AnniversaryEdition.content.title")}
          text={t("tripleBarrelCashBlast2025AnniversaryEdition.content.subtitle")}
          borderColor="#d59947"
          className="mt-4 pb-2"
        />
        <ReactMarkdown>
          {t("tripleBarrelCashBlast2025AnniversaryEdition.content.text")}
        </ReactMarkdown>
        <PrizeTablesTitleBar>
          <h2>{t("tripleBarrelCashBlast2025AnniversaryEdition.content.prizeTablesTitle")}</h2>
        </PrizeTablesTitleBar>
        <PrizeTablesGrid>
          {["diamond", "gold", "silver"].map((key, index) => {
            const table = prizeTables?.[key];
            if (!table) return null;
            return (
              <React.Fragment key={key}>
                {/* Background wrapper spans both rows */}
                <BackgroundWrapper
                  bgImage={useResponsiveImageUrl(
                    `promotions/poker/triple-barrel-cash-blast-2025/${key}-box.png`
                  )}
                  style={{ gridColumn: index + 1, gridRow: '1 / 3' }}
                />

                {/* Table in row 1 */}
                <TableContainer
                  style={{ gridColumn: index + 1, gridRow: 1 }}
                  bgImage={useResponsiveImageUrl(
                    `promotions/poker/triple-barrel-cash-blast-2025/${key}-box.png`
                  )}
                >
                  <StyledTable
                    responsive
                    variant="dark"
                    className={key}
                  >
                    <thead>
                      <tr>
                        <th colSpan={table.columns.length} className={key}>
                          <ResponsiveImage
                            src={`promotions/poker/triple-barrel-cash-blast-2025/${key}-${i18n.language}.png`}
                            alt={table.title}
                          />
                        </th>
                      </tr>
                      <tr>
                        {table.columns.map((col) => (
                          <th key={col.key}>{col.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {table.rows.map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            <ReactMarkdown>{row.envelopes}</ReactMarkdown>
                          </td>
                          <td>{row.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </StyledTable>
                </TableContainer>

                {/* Draw times in row 2 */}
                {table.drawTimesText && (
                  <DrawTimesContainer
                    className={key}
                    style={{ gridColumn: index + 1, gridRow: 2 }}
                  >
                    <ReactMarkdown>{table.drawTimesText}</ReactMarkdown>
                  </DrawTimesContainer>
                )}
              </React.Fragment>
            );
          })}
        </PrizeTablesGrid>
      </container-div>
      <container-div>
        <div className="works-container px-3 pb-3 mt-3">
          <SubTitleSection
            as={"h3"}
            subTitle={t("tripleBarrelCashBlast2025AnniversaryEdition.content.howItWorks.title")}
            text={t("tripleBarrelCashBlast2025AnniversaryEdition.content.howItWorks.text")}
            className="py-0 how-it-works pt-4"
            borderColor="#d59947"
          />
        </div>

        <div className="tabContent my-4 pt-2 pb-4">
          <TournamentSectionalTabs
            subTabs={t("tripleBarrelCashBlast2025AnniversaryEdition.content.sectionalTabs", {
              returnObjects: true,
            })}
          />
          <TournamentSubtabsContent tournamentContent={dynamicSubTabs} />
        </div>
      </container-div>
    </Div>
  );
};

const Div = styled.div`
  --topspacing: 110px;
  --gradientheight: 40px;

  background-color: var(--lightblack);
  color: var(--white);
  text-align: center;
  overflow-x: hidden;
  height: auto;
  padding-bottom: 40px;

  .title-section {
    p {
      font-size: 1.5rem;
      max-width: initial;
    }
  }

  .hero_text_container {
    display: none;
    padding: 0;
  }

  .hero_image_container {
    padding: 20px;
    img {
      max-height: 500px;
    }
  }

  .hero_container.fluid {
    min-height: 440px;
    @media (min-width: 768px) and (max-width: 991px) {
      --topspacing: 40px;
      --gradientheight: 20px;
    }
    @media (max-width: 768px) {
      min-height: 150px;
    }
    @media (min-width: 768px) {
      top: var(--topspacing);
      margin-bottom: var(--topspacing);
      &:before {
        background: linear-gradient(
          to bottom,
          rgba(0, 0, 0, 1) 0%,
          rgba(0, 0, 0, 1) 71.43%,
          rgba(0, 0, 0, 0) 100%
        );
        content: "";
        height: calc(var(--topspacing) + var(--gradientheight));
        top: calc(-1 * var(--topspacing));
        position: relative;
      }
    }
  }

  p,
  li {
    strong {
      color: #d59947;
    }
  }

  li {
    padding-bottom: 0.75rem;
  }

  .works-container {
    background-color: var(--black);
    border-radius: 8px;
    border: 1px solid #634f32c8;
  }

  .how-it-works {
    h3 {
      text-align: left;
      margin-left: 1rem;
      border-color: #d59947;
      margin-bottom: 2rem;
    }
    li,
    p {
      text-align: left;
    }
    li {
      padding-bottom: 0;
    }
  }

  div.periods {
    p {
      font-size: 16px;
    }
    svg {
      margin: 20px 0 10px;
      color: #d59947;
      font-size: 40px;
    }
  }

  .sectional-tournament-tabs {
    justify-content: space-around;
    margin: 20px;
    width: calc(100% - 40px);
    button {
      min-height: unset;
      padding: 0.8rem;
      background: #634f32c8;
      &.active {
        background: #a56e27;
      }
      &:hover {
        background: #a56e27;
      }
    }
    @media (min-width: 768px) {
      button {
        max-width: 24%;
      }
    }
  }

  .tabContent {
    background-color: var(--black);
    h5 {
      margin: 1rem 0 0;
      /* color: #d59947; */
    }
    h4 {
      margin: 0 0 1rem;
      color: #d59947;
    }
    ul {
      padding-top: 0.5rem;
    }
    li {
      padding-bottom: 0;
      margin-bottom: 0;
      p:first-child {
        padding: unset;
        font-size: unset;
        line-height: unset;
        margin: unset;
        max-width: unset;
        display: inline;
      }
      .table-title {
        background-color: var(--mutedred);
        color: var(--white);
        display: inline-block;
        padding: 0.25rem 0.75rem;
        border-radius: 4px;
        margin-bottom: 0.5rem;
      }
    }
    table {
      margin: -0.5rem 0;
    }
  }
`;
const PrizeTablesTitleBar = styled.div`
  background: linear-gradient(180deg, #9a6a29 0%, #d5a64a 100%);
  padding: 0.5rem 2rem;
  margin: 1rem 0 0 0;
  width: 100%;

  h2 {
    color: #fff;
    font-weight: 700;
    text-transform: uppercase;
    margin: 0;
    text-align: center;
    font-size: 2.5rem;
    letter-spacing: 1px;
  }

  @media (max-width: 768px) {
    margin: 2rem 0;
    width: 100%;
    padding: 0.5rem;
    h2 {
      font-size: 1.2rem;
    }
  }
`;

const PrizeTablesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto auto;
  column-gap: 20px;
  row-gap: 0;
  margin: 20px 0;

  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
`;

const BackgroundWrapper = styled.div`
  background: url(${(props) => props.bgImage}) center/100% 100% no-repeat;
  padding: 20px;
  z-index: 0;

  @media (max-width: 767px) {
    display: none;
  }
`;

const TableContainer = styled.div`
  z-index: 1;
  position: relative;
  padding: 20px 20px 0;

  @media (max-width: 767px) {
    background: url(${(props) => props.bgImage || ''}) center/100% 100% no-repeat;
    padding: 20px 0 0;
  }
`;

const StyledTable = styled(Table)`
  width: 100%;
  position: relative;
  border: none !important;
  border-collapse: collapse;
  border-spacing: 0;
  background: transparent !important;

  tbody {
    background: transparent !important;
  }

  thead,
  th,
  td {
    border: none !important;
    background: transparent !important;
    strong {
      color: white;
    }
    p {
      margin: 0;
    }
  }
  img {
    margin: 2px 0 6px 0;
    max-height: 32px;
    width: 100%;
  }
  .diamond > img {
    margin: -6px 0;
    max-height: 52px;
    width: 100%;
  }
  th {
    vertical-align: middle;
    max-height: 40px;
    width: 100%;
  }
  th {
    vertical-align: middle;
  }
  tr {
    display: table;
    width: 100%;
    table-layout: fixed;
  }

  thead tr:first-child th {
    padding: 0 0 1rem 0;
  }

  thead tr:nth-child(2) th {
    font-weight: 700;
    color: #fff;
    background: linear-gradient(180deg, #9a6a29 0%, #d5a64a 100%) !important;
    position: relative;

    &:not(:last-child)::after {
      content: "|";
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      color: #fff;
      font-weight: bold;
      font-size: 1.2rem;
    }
  }

  tbody tr:first-child td {
    padding-top: 1rem;
  }

  tbody tr:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const DrawTimesContainer = styled.div`
  z-index: 1;
  position: relative;
  text-align: center;
  padding: 1rem 20px;
  background: #0000003d;

  p {
    margin: 0;
    strong {
      color: #d59947;
    }
  }

  @media (max-width: 767px) {
    margin-bottom: 20px;
    margin-top: -20px;
  }
`;

export default TripleBarrelCashBlast2025AnniversaryEdition;
